/**
 * Authentication Service - MSAL authentication handling
 */

import * as msal from "@azure/msal-browser";
import { msalConfig, loginRequest } from "../config/authConfig.js";

let msalInstance = null;
let account = null;
let currentUserEmail = null;

// Cached token for backend API calls
let _apiToken = null;
let _apiTokenExpiry = 0;

/**
 * Acquire a cached MSAL access token to authenticate against the backend.
 * The backend validates this token against Microsoft Graph.
 */
export async function getApiToken() {
  if (_apiToken && Date.now() < _apiTokenExpiry - 60_000) return _apiToken;
  if (!msalInstance) throw new Error("MSAL nicht initialisiert");

  const acc = account || msalInstance.getActiveAccount() || (msalInstance.getAllAccounts()[0] ?? null);

  let result;
  try {
    if (acc) {
      result = await msalInstance.acquireTokenSilent({ ...loginRequest, account: acc });
    } else {
      // No account in MSAL cache (e.g. Excel add-in WebView after restart) —
      // try SSO silent with email hint before forcing a popup
      const hint = currentUserEmail || localStorage.getItem("userEmail");
      result = await msalInstance.ssoSilent({ ...loginRequest, ...(hint ? { loginHint: hint } : {}) });
      account = result.account;
      msalInstance.setActiveAccount(account);
    }
  } catch (err) {
    if (err instanceof msal.InteractionRequiredAuthError || !acc) {
      result = await msalInstance.acquireTokenPopup({ ...loginRequest, ...(acc ? { account: acc } : {}) });
      account = result.account;
      msalInstance.setActiveAccount(account);
    } else {
      throw err;
    }
  }

  _apiToken = result.accessToken;
  _apiTokenExpiry = result.expiresOn ? result.expiresOn.getTime() : Date.now() + 3_600_000;
  return _apiToken;
}

export async function initializeMsal() {
  msalInstance = new msal.PublicClientApplication(msalConfig);
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();
  return msalInstance;
}

export function getMsalInstance() {
  return msalInstance;
}

export function getAccount() {
  return account;
}

export function setAccount(acc) {
  account = acc;
}

export function getCurrentUserEmail() {
  return currentUserEmail;
}

export function setCurrentUserEmail(email) {
  currentUserEmail = email;
}

export async function checkExistingSession() {
  const currentAccounts = msalInstance.getAllAccounts();
  if (currentAccounts.length > 0) {
    account = currentAccounts[0];
    msalInstance.setActiveAccount(account);
    return true;
  }
  
  // Check localStorage as fallback
  const storedEmail = localStorage.getItem("userEmail");
  if (storedEmail) {
    currentUserEmail = storedEmail;
    const userName = localStorage.getItem("userName");
    if (userName) {
      return { restored: true, userName, email: currentUserEmail };
    }
  }
  
  return false;
}

export async function loadUserInfo(updateUserDisplayFn, updateUIFn) {
  // Try to restore from localStorage first
  currentUserEmail = localStorage.getItem("userEmail");
  const userName = localStorage.getItem("userName");
  
  if (currentUserEmail && userName) {
    updateUserDisplayFn(userName, currentUserEmail);
    updateUIFn(true);
    return;
  }
  
  // Fetch from Graph API if not in localStorage
  try {
    const token = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account
    });
    
    const response = await fetch("https://graph.microsoft.com/v1.0/me", {
      headers: { Authorization: `Bearer ${token.accessToken}` }
    });
    
    const user = await response.json();
    currentUserEmail = user.mail || user.userPrincipalName;
    
    // Store in localStorage
    localStorage.setItem("userEmail", currentUserEmail);
    localStorage.setItem("userName", user.displayName);
    
    updateUserDisplayFn(user.displayName, currentUserEmail);
    updateUIFn(true);
  } catch (error) {
    console.error("Error fetching user info:", error);
    updateUIFn(false);
  }
}

export async function login(updateUserDisplayFn, updateUIFn) {
  try {
    const loginResponse = await msalInstance.loginPopup(loginRequest);
    account = loginResponse.account;
    msalInstance.setActiveAccount(account);
    
    // Get user info
    const token = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account
    });
    
    const response = await fetch("https://graph.microsoft.com/v1.0/me", {
      headers: { Authorization: `Bearer ${token.accessToken}` }
    });
    
    const user = await response.json();
    currentUserEmail = user.mail || user.userPrincipalName;
    
    // Store in localStorage for persistence
    localStorage.setItem("userEmail", currentUserEmail);
    localStorage.setItem("userName", user.displayName);
    
    updateUserDisplayFn(user.displayName, currentUserEmail);
    updateUIFn(true);
    
    return true;
  } catch (error) {
    console.error("Error during login:", error);
    return false;
  }
}

export async function logout(updateUIFn, clearStateFn) {
  try {
    await msalInstance.logoutPopup();
    
    // Clear state
    account = null;
    currentUserEmail = null;
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userName");
    
    clearStateFn();
    updateUIFn(false);
    
    // Update UI elements
    document.getElementById("user-info").classList.add("hidden");
    document.getElementById("logout").classList.add("hidden");
    document.getElementById("chat-interface").classList.add("hidden");
    document.getElementById("chat-messages").innerHTML = "";
    
    return true;
  } catch (error) {
    console.error("Error during logout:", error);
    return false;
  }
}